import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Upload, RefreshCw, Loader, Database, Sparkles, ExternalLink } from 'lucide-react';
import {
  getCiqualMeta,
  previewCiqualImport,
  commitCiqualImport,
  previewMassSync,
  runMassSync,
  previewBulkMatch,
  runBulkMatch,
  CiqualImportPreview,
  CiqualImportReport,
  BulkMatchPreview,
  BulkMatchReport,
} from '../../../utils/nutritionApi';

export function CiqualNutritionPage() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState<any>(null);
  const [file, setFile] = useState<File | null>(null);
  const [sourceVersion, setSourceVersion] = useState('CIQUAL_2025');
  const [purgeMissing, setPurgeMissing] = useState(false);
  const [preview, setPreview] = useState<CiqualImportPreview | null>(null);
  const [report, setReport] = useState<CiqualImportReport | null>(null);
  const [massPreview, setMassPreview] = useState<Record<string, unknown> | null>(null);
  const [massReport, setMassReport] = useState<Record<string, unknown> | null>(null);
  const [bulkLang, setBulkLang] = useState<'all' | 'fr' | 'en'>('fr');
  const [bulkPreview, setBulkPreview] = useState<BulkMatchPreview | null>(null);
  const [bulkReport, setBulkReport] = useState<BulkMatchReport | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshMeta = async () => {
    try {
      const res = await getCiqualMeta();
      setMeta(res.meta);
      if (res.meta?.currentSourceVersion) {
        setSourceVersion(res.meta.currentSourceVersion);
      }
    } catch (err) {
      console.warn(err);
    }
  };

  useEffect(() => {
    refreshMeta();
  }, []);

  const withBusy = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3">
        <Database className="h-6 w-6 text-menioo-green" />
        <div>
          <h2 className="text-xl font-display font-bold text-menioo-navy">CIQUAL / Nutrition</h2>
          <p className="text-sm text-gray-500">
            Import ANSES CIQUAL, matching ingrédients, mass sync. Nutri-Score calcul hors scope.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <p>
          Version courante :{' '}
          <span className="font-medium text-menioo-navy">
            {meta?.currentSourceVersion || '— aucune importée'}
          </span>
        </p>
        {meta?.lastImport && (
          <p className="mt-1">
            Dernier import : {meta.lastImport.written} écrits ({meta.lastImport.added} ajoutés,{' '}
            {meta.lastImport.updated} maj)
            {meta.lastImport.deleted ? `, ${meta.lastImport.deleted} supprimés` : ''}
          </p>
        )}
      </div>

      <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-display font-bold text-menioo-navy">1. Import table CIQUAL</h3>
        <p className="text-sm text-gray-500">
          Upload du fichier XLSX officiel ANSES (sheet composition). Groupe « aliments infantiles »
          exclu. Valeurs « - » → null, « traces » → 0.01.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-menioo-navy mb-1">Fichier XLSX</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                setPreview(null);
                setReport(null);
              }}
              className="block w-full text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-menioo-navy mb-1">sourceVersion</label>
            <input
              value={sourceVersion}
              onChange={(e) => setSourceVersion(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-menioo-green focus:ring-menioo-green sm:text-sm"
            />
          </div>
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-menioo-navy">
          <input
            type="checkbox"
            checked={purgeMissing}
            onChange={(e) => setPurgeMissing(e.target.checked)}
            className="rounded border-gray-300 text-menioo-green focus:ring-menioo-green"
          />
          Supprimer les codes absents du nouveau fichier (purge)
        </label>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            disabled={!file || !!busy}
            onClick={() =>
              withBusy('preview', async () => {
                if (!file) return;
                const res = await previewCiqualImport(file, sourceVersion);
                setPreview(res.preview);
              })
            }
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-menioo-navy bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
          >
            {busy === 'preview' ? (
              <Loader className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Prévisualiser le diff
          </button>
          <button
            type="button"
            disabled={!file || !preview || !!busy}
            onClick={() => {
              if (
                !window.confirm(
                  `Importer ${preview?.totalParsed} aliments (${preview?.added} ajoutés, ${preview?.modified} modifiés, ${preview?.removed} absents) ?`
                )
              ) {
                return;
              }
              withBusy('import', async () => {
                if (!file) return;
                const res = await commitCiqualImport(file, sourceVersion, purgeMissing);
                setReport(res.report);
                await refreshMeta();
              });
            }}
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-menioo-green hover:bg-menioo-green-dark disabled:opacity-50"
          >
            {busy === 'import' ? (
              <Loader className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            Confirmer l&apos;import
          </button>
        </div>

        {preview && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>Parsés : {preview.totalParsed}</div>
            <div>Ajoutés : {preview.added}</div>
            <div>Modifiés : {preview.modified}</div>
            <div>Inchangés : {preview.unchanged}</div>
            <div>Absents : {preview.removed}</div>
            <div>Infantiles exclus : {preview.skippedInfant}</div>
            <div className="col-span-2">Sheet : {preview.sheetName}</div>
          </div>
        )}

        {report && (
          <div className="rounded-md bg-green-50 border border-green-200 p-4 text-sm text-green-900">
            Import OK — écrits {report.written} (ajoutés {report.added}, maj {report.updated}
            {report.deleted ? `, supprimés ${report.deleted}` : ''}). Infantiles exclus :{' '}
            {report.skippedInfant}.
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-display font-bold text-menioo-navy">
          2. Mass sync → standardIngredients
        </h3>
        <p className="text-sm text-gray-500">
          Met à jour les ingrédients liés CIQUAL sans <code>manualOverride</code>. Codes disparus →
          statut <code>stale</code> (données conservées).
        </p>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            disabled={!!busy}
            onClick={() =>
              withBusy('massPreview', async () => {
                const res = await previewMassSync();
                setMassPreview(res.preview);
              })
            }
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-menioo-navy bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
          >
            {busy === 'massPreview' ? (
              <Loader className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Prévisualiser
          </button>
          <button
            type="button"
            disabled={!massPreview || !!busy}
            onClick={() => {
              if (!window.confirm('Lancer le mass sync nutrition ?')) return;
              withBusy('massRun', async () => {
                const res = await runMassSync();
                setMassReport(res.report);
              });
            }}
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-menioo-green hover:bg-menioo-green-dark disabled:opacity-50"
          >
            {busy === 'massRun' ? (
              <Loader className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Exécuter le sync
          </button>
        </div>

        {massPreview && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(massPreview).map(([k, v]) => (
              <div key={k}>
                {k}: <strong>{String(v)}</strong>
              </div>
            ))}
          </div>
        )}

        {massReport && (
          <div className="rounded-md bg-green-50 border border-green-200 p-4 text-sm">
            Sync terminé — updated {String(massReport.updated)}, stale {String(massReport.stale)},
            skippedManual {String(massReport.skippedManual)}.
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-display font-bold text-menioo-navy flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-menioo-green" />
          3. Bulk match CIQUAL
        </h3>
        <p className="text-sm text-gray-500">
          Calcule une proposition CIQUAL pour chaque ingrédient sans nutrition, avec un niveau de
          certitude. Rien n&apos;est écrit tant que vous ne validez pas dans l&apos;écran de review.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <label className="text-sm text-menioo-navy">
            Langue{' '}
            <select
              value={bulkLang}
              onChange={(e) => setBulkLang(e.target.value as 'all' | 'fr' | 'en')}
              className="ml-1 rounded-md border-gray-300 text-sm"
            >
              <option value="fr">FR</option>
              <option value="en">EN</option>
              <option value="all">Toutes</option>
            </select>
          </label>
          <Link
            to="/admin/mass-db/ciqual/match-review"
            className="inline-flex items-center text-sm text-menioo-green hover:underline"
          >
            Ouvrir la review
            <ExternalLink className="h-3.5 w-3.5 ml-1" />
          </Link>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            disabled={!!busy}
            onClick={() =>
              withBusy('bulkPreview', async () => {
                const res = await previewBulkMatch({
                  language: bulkLang === 'all' ? null : bulkLang,
                });
                setBulkPreview(res.preview);
              })
            }
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-menioo-navy bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
          >
            {busy === 'bulkPreview' ? (
              <Loader className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            Prévisualiser
          </button>
          <button
            type="button"
            disabled={!bulkPreview || !!busy}
            onClick={() => {
              if (
                !window.confirm(
                  `Lancer le matching pour ${bulkPreview?.scannedEligible} ingrédients ?`
                )
              ) {
                return;
              }
              withBusy('bulkRun', async () => {
                const res = await runBulkMatch({
                  language: bulkLang === 'all' ? null : bulkLang,
                });
                setBulkReport(res.report);
                if (res.report.runId) {
                  navigate(`/admin/mass-db/ciqual/match-review?runId=${res.report.runId}`);
                }
              });
            }}
            className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-menioo-green hover:bg-menioo-green-dark disabled:opacity-50"
          >
            {busy === 'bulkRun' ? (
              <Loader className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            Lancer le matching
          </button>
        </div>

        {(busy === 'bulkPreview' || busy === 'bulkRun') && (
          <p className="text-sm text-amber-700">
            Traitement en cours
            {bulkPreview?.scannedEligible != null
              ? ` (${bulkPreview.scannedEligible} ingrédients)`
              : ''}
            . Selon le volume, ça peut prendre du temps.
          </p>
        )}

        {bulkPreview && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-4 text-sm grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div>
              Éligibles : <strong>{bulkPreview.scannedEligible}</strong>
            </div>
            <div>
              Déjà matchés : {bulkPreview.skippedAlreadyMatched}
            </div>
            <div>
              Certitude élevée : <strong>{bulkPreview.byConfidence.high}</strong>
            </div>
            <div>
              Moyenne : <strong>{bulkPreview.byConfidence.medium}</strong>
            </div>
            <div>
              Faible : <strong>{bulkPreview.byConfidence.low}</strong>
            </div>
            <div>
              Aucune : <strong>{bulkPreview.byConfidence.none}</strong>
            </div>
          </div>
        )}

        {bulkReport && (
          <div className="rounded-md bg-green-50 border border-green-200 p-4 text-sm">
            Matching prêt — {bulkReport.queued} en file de review (skippés {bulkReport.skipped}).
          </div>
        )}
      </section>
    </div>
  );
}
